import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { LegacyTabPanelComponent } from './legacy-tabpanel.component';

export interface LegacyTabCloseEvent {
  index: number;
  close: () => void;
}

export interface LegacyTabChangeEvent {
  index: number;
}

@Component({
  standalone: false,
  selector: 'p-tabView',
  template: `
    <div class="p-tabview p-component">
      <ul class="p-tabview-nav" role="tablist">
        <li
          *ngFor="let tab of tabPanels; let i = index"
          role="presentation"
          [class.p-highlight]="tab.selected"
          [class.p-disabled]="tab.disabled"
        >
          <a
            role="tab"
            class="p-tabview-nav-link"
            [class.p-tabview-selected]="tab.selected"
            [attr.aria-selected]="tab.selected"
            [attr.tabindex]="tab.disabled ? -1 : 0"
            (click)="onTabHeaderClick($event, i)"
          >
            <span class="p-tabview-title">{{ tab.header }}</span>
            <span
              *ngIf="tab.closable"
              class="p-tabview-close pi pi-times"
              (click)="onTabCloseClick($event, i)"
            ></span>
          </a>
        </li>
      </ul>
      <div class="p-tabview-panels">
        <ng-content></ng-content>
      </div>
    </div>
  `,
})
export class LegacyTabViewComponent implements AfterContentInit {
  @ContentChildren(LegacyTabPanelComponent) private _tabPanels!: QueryList<LegacyTabPanelComponent>;

  @Input() controlClose = false;
  @Output() onClose = new EventEmitter<LegacyTabCloseEvent>();
  @Output() onChange = new EventEmitter<LegacyTabChangeEvent>();

  constructor(private cdr: ChangeDetectorRef) {}

  get tabs(): LegacyTabPanelComponent[] {
    return this._tabPanels?.toArray() ?? [];
  }

  get tabPanels(): LegacyTabPanelComponent[] {
    return this.tabs;
  }

  ngAfterContentInit(): void {
    this.ensureSelection();
    this._tabPanels.changes.subscribe(() => {
      this.ensureSelection();
      this.cdr.markForCheck();
    });
  }

  onTabHeaderClick(event: MouseEvent, index: number): void {
    event.preventDefault();
    const tab = this.tabs[index];
    if (!tab || tab.disabled) {
      return;
    }
    this.selectTab(index);
    this.onChange.emit({ index });
  }

  onTabCloseClick(event: MouseEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();

    const close = () => {
      this.cdr.markForCheck();
    };

    if (this.controlClose) {
      this.onClose.emit({ index, close });
    } else {
      this.onClose.emit({ index, close });
      close();
    }
  }

  private selectTab(index: number): void {
    this.tabs.forEach((panel, i) => {
      panel.selected = i === index;
    });
    this.cdr.markForCheck();
  }

  private ensureSelection(): void {
    const panels = this.tabs;
    if (!panels.length) {
      return;
    }
    if (panels.some((panel) => panel.selected)) {
      return;
    }
    const firstEnabled = panels.findIndex((panel) => !panel.disabled);
    if (firstEnabled >= 0) {
      this.selectTab(firstEnabled);
    }
  }
}

export { LegacyTabViewComponent as TabView };
